import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Loader2, Play, CheckCircle2, AlertTriangle, ShieldCheck, ShieldAlert, XCircle, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import type { Dwelling, Validation, Stitch } from "@/api/types";
import { StatusBadge } from "@/components/StatusBadge";

const SEV = {
  critical: { icon: ShieldAlert, cls: "border-rose-500/40 bg-rose-500/5", text: "text-rose-400", label: "Critique" },
  warning: { icon: AlertTriangle, cls: "border-amber-500/40 bg-amber-500/5", text: "text-amber-400", label: "Avertissement" },
  info: { icon: CheckCircle2, cls: "border-sky-500/40 bg-sky-500/5", text: "text-sky-400", label: "Info" },
} as const;

export default function ValidationPage() {
  const { dwellingId = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: dwelling } = useQuery<Dwelling>({ queryKey: ["dwelling", dwellingId], queryFn: () => api.getDwelling(dwellingId) });
  const { data: validations = [], isLoading } = useQuery<Validation[]>({
    queryKey: ["validations", dwelling?.project_id, dwellingId],
    enabled: !!dwelling,
    queryFn: async () => (await api.listValidations(dwelling!.project_id)).filter((v) => v.dwelling_id === dwellingId),
  });
  const { data: stitches = [] } = useQuery<Stitch[]>({ queryKey: ["stitches", dwellingId], queryFn: () => api.listStitches(dwellingId) });

  const runMut = useMutation({
    mutationFn: () => api.runValidation(dwellingId),
    onSuccess: (r) => { toast.success(`Statut: ${r.validation_status}`); qc.invalidateQueries({ queryKey: ["validations"] }); qc.invalidateQueries({ queryKey: ["dwelling", dwellingId] }); },
    onError: (e: any) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const updateMut = useMutation({
    mutationFn: (p: { id: string; status: string }) => api.updateValidation(p.id, p.status),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["validations"] }); qc.invalidateQueries({ queryKey: ["dwelling", dwellingId] }); },
    onError: (e: any) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (!dwelling) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const criticalsUnresolved = validations.filter((v) => v.severity === "critical" && ["pending", "rejected", "blocked"].includes(v.status));
  const isValidated = dwelling.validation_status === "validated";

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(`/dwellings/${dwellingId}`)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Logement
      </button>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Espace de validation</h1>
          <p className="mt-1 text-sm text-muted-foreground">{dwelling.name} — contrôle des incohérences critiques avant export.</p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={dwelling.validation_status} testId="dwelling-validation-badge" />
          <button data-testid="rerun-validation-button" onClick={() => runMut.mutate()} disabled={runMut.isPending} className="flex items-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50">
            {runMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Relancer
          </button>
        </div>
      </div>

      {isValidated ? (
        <div data-testid="validation-pass-banner" className="flex items-center gap-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-5">
          <ShieldCheck className="h-6 w-6 text-emerald-400" />
          <div>
            <div className="font-semibold text-emerald-400">Validation réussie</div>
            <div className="text-sm text-muted-foreground">Aucune incohérence critique. Les géométries sont validées et l'export est autorisé.</div>
          </div>
          <button onClick={() => navigate(`/export/${dwellingId}`)} className="ml-auto flex items-center gap-2 rounded-md bg-emerald-500 px-4 py-2 text-sm font-semibold text-background hover:opacity-90">
            <PackageCheck className="h-4 w-4" /> Exporter
          </button>
        </div>
      ) : criticalsUnresolved.length > 0 ? (
        <div data-testid="validation-blocked-banner" className="flex items-center gap-3 rounded-xl border border-rose-500/40 bg-rose-500/10 p-5">
          <ShieldAlert className="h-6 w-6 animate-pulse text-rose-400" />
          <div>
            <div className="font-semibold text-rose-400">Export bloqué</div>
            <div className="text-sm text-muted-foreground">{criticalsUnresolved.length} incohérence(s) critique(s) non résolue(s).</div>
          </div>
        </div>
      ) : validations.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground">Aucune validation. Lancez une validation pour analyser ce logement.</div>
      ) : null}

      <div data-testid="validation-issues-list" className="space-y-3">
        {isLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : validations.map((v) => {
          const sev = SEV[v.severity];
          return (
            <div key={v._id} data-testid={`validation-${v._id}`} className={`flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between ${sev.cls}`}>
              <div className="flex items-start gap-3">
                <sev.icon className={`mt-0.5 h-5 w-5 ${sev.text}`} />
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`uppercase-label ${sev.text}`}>{sev.label}</span>
                    <StatusBadge status={v.status} />
                  </div>
                  <p className="mt-1 text-sm">{v.message}</p>
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <button data-testid={`issue-approve-${v._id}`} onClick={() => updateMut.mutate({ id: v._id, status: "approved" })} className="flex items-center gap-1.5 rounded-md bg-emerald-500/15 px-3 py-1.5 text-sm text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25">
                  <CheckCircle2 className="h-4 w-4" /> Approuver
                </button>
                <button data-testid={`issue-reject-${v._id}`} onClick={() => updateMut.mutate({ id: v._id, status: "rejected" })} className="flex items-center gap-1.5 rounded-md bg-rose-500/15 px-3 py-1.5 text-sm text-rose-400 border border-rose-500/30 hover:bg-rose-500/25">
                  <XCircle className="h-4 w-4" /> Rejeter
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-xl border border-border/60 bg-card p-5">
        <h3 className="mb-3 text-lg font-semibold">Statut des assemblages</h3>
        {stitches.length === 0 ? <p className="text-sm text-muted-foreground">Aucun assemblage.</p> : (
          <div className="space-y-2">
            {stitches.map((s) => (
              <div key={s._id} className="flex items-center justify-between rounded-lg border border-border/50 bg-background/40 p-3 text-sm">
                <span>{s.room_a_name} ↔ {s.room_b_name}</span>
                <StatusBadge status={s.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
